import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Copy, Check, ChevronDown, ChevronUp, Database, Download } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';
import { useSession } from '../auth/SessionContext.jsx';

function fmtDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('fr-FR');
}

function fmtRelative(targetMs, nowMs = Date.now()) {
  if (!targetMs) return '';
  const diff = targetMs - nowMs;
  const abs = Math.abs(diff);
  const days = Math.round(abs / (24 * 3600 * 1000));
  if (diff >= 0) {
    if (days === 0) return "expire aujourd'hui";
    if (days === 1) return 'dans 1 jour';
    return `dans ${days} jours`;
  } else {
    if (days === 0) return "expiré aujourd'hui";
    if (days === 1) return 'expiré il y a 1 jour';
    return `expiré il y a ${days} jours`;
  }
}

function maskedPrefix(prefix) {
  // Affiche les 2 premiers caractères, le reste masqué : "XX****"
  const head = (prefix || '').slice(0, 2).toUpperCase();
  return `${head || 'XX'}****`;
}

function statusOf(c) {
  if (c.revoked) return 'revoked';
  if (c.expires_at && c.expires_at < Date.now()) return 'expired';
  return 'active';
}

function StatusBadge({ s }) {
  const map = {
    active:  { label: 'Actif',   bg: '#2EB6A7', color: '#fff' },
    revoked: { label: 'Révoqué', bg: '#8a93a3', color: '#fff' },
    expired: { label: 'Expiré',  bg: '#c0392b', color: '#fff' },
  };
  const meta = map[s] || map.active;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      background: meta.bg, color: meta.color, fontSize: 12, fontWeight: 700,
    }}>{meta.label}</span>
  );
}

export default function AdminCodes() {
  const { session } = useSession();

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [clientName, setClientName] = useState('');
  const [days, setDays] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [newCode, setNewCode] = useState(null); // { code, code_prefix, client_name, expires_at }
  const [copied, setCopied] = useState(false);

  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);

  // Double-check guard.
  if (session && session.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const loadCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/admin/codes');
      setCodes(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCodes(); }, []);

  const onGenerate = async (e) => {
    e.preventDefault();
    setGenError(null);
    const name = clientName.trim();
    if (!name) { setGenError('Nom client requis'); return; }
    const d = Number(days);
    if (!Number.isFinite(d) || d < 1 || d > 3650) {
      setGenError('Durée invalide (1 à 3650 jours)');
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post('/api/admin/codes', { client_name: name, days: d });
      setNewCode(res);
      setCopied(false);
      setClientName('');
      setDays(30);
      loadCodes();
    } catch (e) {
      setGenError(e.message || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const onCopy = async () => {
    if (!newCode?.code) return;
    try {
      await navigator.clipboard.writeText(newCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = newCode.code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      catch {}
      document.body.removeChild(ta);
    }
  };

  const onRevoke = async (id) => {
    if (!window.confirm('Révoquer ce code ? Les utilisateurs ne pourront plus s\'en servir pour se connecter.')) return;
    try {
      await api.post(`/api/admin/codes/${id}/revoke`, {});
      loadCodes();
    } catch (e) {
      alert('Révocation impossible : ' + (e.message || 'erreur'));
    }
  };

  const toggleLogs = async () => {
    const next = !logsOpen;
    setLogsOpen(next);
    if (next && logs === null) {
      setLogsLoading(true);
      try {
        const data = await api.get('/api/admin/login-logs?limit=50');
        setLogs(Array.isArray(data?.items) ? data.items : []);
      } catch {
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    }
  };

  return (
    <div className="vrt-codes">
      <Header />

      <main className="vrt-codes__main">
        <div className="vrt-codes__head">
          <h1 className="vrt-codes__title">Codes d'accès clients</h1>
          <a
            href="/api/admin/backup"
            className="vrt-btn vrt-btn--ghost vrt-codes__backup"
            title="Télécharger une sauvegarde complète (base + photos)"
          >
            <Database size={16} />
            <span>Sauvegarder la base</span>
            <Download size={14} />
          </a>
        </div>

        <section className="vrt-card vrt-codes__gen" aria-label="Générer un nouveau code">
          <h2 className="vrt-codes__h2">Générer un nouveau code</h2>

          <form onSubmit={onGenerate} className="vrt-codes__form">
            <label className="vrt-codes__field">
              <span>Nom du client</span>
              <input
                type="text"
                className="vrt-input"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Ex : Mairie de Grasse"
                required
              />
            </label>

            <label className="vrt-codes__field vrt-codes__field--small">
              <span>Validité (jours)</span>
              <input
                type="number"
                className="vrt-input"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                min={1}
                max={3650}
                required
              />
            </label>

            <button
              type="submit"
              className="vrt-btn vrt-btn--teal"
              disabled={generating}
            >
              {generating ? 'Génération…' : 'Générer'}
            </button>
          </form>

          {genError && (
            <div className="vrt-codes__error" role="alert">{genError}</div>
          )}

          {newCode && (
            <div className="vrt-codes__alert" role="status">
              <div className="vrt-codes__alert-head">
                Code généré pour <strong>{newCode.client_name}</strong>
                {newCode.expires_at && <> · expire le {fmtDate(newCode.expires_at)}</>}
              </div>
              <div className="vrt-codes__codebox">
                <code className="vrt-codes__bigcode">{newCode.code}</code>
                <button
                  type="button"
                  className="vrt-btn vrt-btn--ghost vrt-codes__copy"
                  onClick={onCopy}
                  aria-label="Copier le code"
                >
                  {copied ? <><Check size={18}/> Copié</> : <><Copy size={18}/> Copier</>}
                </button>
              </div>
              <div className="vrt-codes__alert-foot">
                Ce code ne sera plus affiché — copiez-le maintenant.
              </div>
            </div>
          )}
        </section>

        <section aria-label="Liste des codes" style={{ marginTop: 24 }}>
          <h2 className="vrt-codes__h2">Codes existants</h2>

          {error && <div className="vrt-codes__error" role="alert">Erreur : {error}</div>}

          {loading ? (
            <div className="vrt-codes__empty">Chargement…</div>
          ) : codes.length === 0 ? (
            <div className="vrt-codes__empty">Aucun code généré pour le moment.</div>
          ) : (
            <div className="vrt-codes__tablewrap">
              <table className="vrt-codes__table">
                <thead>
                  <tr>
                    <th>Préfixe</th>
                    <th>Client</th>
                    <th>Créé</th>
                    <th>Expire</th>
                    <th>Statut</th>
                    <th>Avaloirs</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => {
                    const s = statusOf(c);
                    return (
                      <tr key={c.id}>
                        <td><code className="vrt-codes__prefix">{maskedPrefix(c.code_prefix)}</code></td>
                        <td>{c.client_name || '—'}</td>
                        <td>{fmtDate(c.created_at)}</td>
                        <td>
                          {fmtDate(c.expires_at)}
                          {c.expires_at && (
                            <div style={{ fontSize: 11, color: 'var(--vrt-text-muted)' }}>
                              {fmtRelative(c.expires_at)}
                            </div>
                          )}
                        </td>
                        <td><StatusBadge s={s} /></td>
                        <td>{c.avaloirs_count ?? 0}</td>
                        <td>
                          {s === 'active' ? (
                            <button
                              type="button"
                              className="vrt-codes__revoke"
                              onClick={() => onRevoke(c.id)}
                            >
                              Révoquer
                            </button>
                          ) : (
                            <span style={{ color: 'var(--vrt-text-muted)', fontSize: 13 }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="vrt-codes__logs" aria-label="Logs de connexion">
          <button
            type="button"
            className="vrt-codes__logs-toggle"
            onClick={toggleLogs}
            aria-expanded={logsOpen}
          >
            {logsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            <span>Derniers logs de connexion</span>
          </button>
          {logsOpen && (
            <div className="vrt-codes__logs-body">
              {logsLoading ? (
                <div className="vrt-codes__empty">Chargement…</div>
              ) : !logs || logs.length === 0 ? (
                <div className="vrt-codes__empty">Aucun log disponible.</div>
              ) : (
                <div className="vrt-codes__tablewrap">
                  <table className="vrt-codes__table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Préfixe code</th>
                        <th>Client</th>
                        <th>IP</th>
                        <th>Résultat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l, i) => (
                        <tr key={l.id ?? i}>
                          <td>{fmtDate(l.created_at || l.timestamp)}</td>
                          <td><code className="vrt-codes__prefix">{l.code_prefix || '—'}</code></td>
                          <td>{l.client_name || '—'}</td>
                          <td>{l.ip || '—'}</td>
                          <td>{l.success === false ? 'Échec' : 'OK'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      <Footer />

      <style>{`
        .vrt-codes { min-height: 100vh; background: var(--vrt-bg); }
        .vrt-codes__main { max-width: 1200px; margin: 0 auto; padding: 20px 16px 60px; }
        .vrt-codes__head {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap; margin-bottom: 18px;
        }
        .vrt-codes__title { margin: 0; font-size: 22px; color: var(--vrt-navy); }
        .vrt-codes__backup {
          padding: 0 14px; min-height: 40px; gap: 8px; font-size: 13px;
        }
        .vrt-codes__h2 { margin: 0 0 12px; font-size: 16px; color: var(--vrt-navy); }
        .vrt-codes__form {
          display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;
        }
        .vrt-codes__field { flex: 1 1 220px; display: flex; flex-direction: column; gap: 4px; }
        .vrt-codes__field span { font-size: 13px; color: var(--vrt-text-muted); font-weight: 600; }
        .vrt-codes__field--small { flex: 0 1 160px; }
        .vrt-codes__error {
          background: #fdecea; color: #a23123; padding: 10px 14px;
          border-radius: 8px; margin-top: 12px;
        }
        .vrt-codes__alert {
          margin-top: 16px;
          background: var(--vrt-teal-100);
          border: 1px solid var(--vrt-teal);
          border-radius: 12px;
          padding: 16px;
        }
        .vrt-codes__alert-head { color: var(--vrt-navy); font-size: 14px; margin-bottom: 10px; }
        .vrt-codes__alert-foot { color: var(--vrt-teal-700); font-size: 12px; margin-top: 10px; font-weight: 600; }
        .vrt-codes__codebox {
          display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
          background: #fff; border: 1px solid var(--vrt-teal); border-radius: 10px;
          padding: 12px 14px;
        }
        .vrt-codes__bigcode {
          font-family: 'Menlo', 'Consolas', 'Courier New', monospace;
          font-size: 22px; font-weight: 700; color: var(--vrt-navy);
          letter-spacing: 1px; user-select: all; word-break: break-all; flex: 1 1 auto;
        }
        .vrt-codes__copy { min-height: 44px; padding: 0 16px; flex: 0 0 auto; }
        .vrt-codes__tablewrap {
          overflow-x: auto; border: 1px solid var(--vrt-border); border-radius: 12px;
        }
        .vrt-codes__table {
          width: 100%; border-collapse: collapse; font-size: 14px; min-width: 760px;
        }
        .vrt-codes__table th, .vrt-codes__table td {
          padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--vrt-border);
          vertical-align: middle;
        }
        .vrt-codes__table thead th {
          background: var(--vrt-bg-alt); color: var(--vrt-navy);
          font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .04em;
        }
        .vrt-codes__prefix {
          font-family: 'Menlo', 'Consolas', monospace; font-size: 13px; color: var(--vrt-navy);
        }
        .vrt-codes__revoke {
          padding: 6px 10px; border-radius: 8px; border: 1px solid var(--vrt-danger);
          background: #fff; color: var(--vrt-danger); font-weight: 600;
          font-size: 13px; min-height: 36px;
        }
        .vrt-codes__revoke:hover { background: var(--vrt-danger); color: #fff; }
        .vrt-codes__empty {
          padding: 24px; text-align: center; color: var(--vrt-text-muted);
          background: var(--vrt-bg-alt); border-radius: 12px;
        }
        .vrt-codes__logs { margin-top: 28px; }
        .vrt-codes__logs-toggle {
          display: inline-flex; align-items: center; gap: 8px;
          background: transparent; border: 1px solid var(--vrt-border);
          color: var(--vrt-navy); border-radius: 10px;
          padding: 10px 14px; font-weight: 600; font-size: 14px;
        }
        .vrt-codes__logs-toggle:hover { background: var(--vrt-bg-alt); }
        .vrt-codes__logs-body { margin-top: 12px; }
      `}</style>
    </div>
  );
}
